/**
 * UpgradeSystem — buying passive biomass upgrades, recomputing stats.
 *
 * Cost of upgrade `id` at current level N: floor(baseCost * costScale^N).
 * Levels are stored in saveManager.getRun().upgrades and survive across
 * sessions (but reset on prestige).
 *
 * Emits 'upgrade:bought' so the HUD and save manager can react.
 */

import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import type { UpgradeDef } from '@/data/types';
import { player } from './PlayerState';
import type { MutationSystem } from './MutationSystem';

export class UpgradeSystem {
  constructor(
    private defs: Record<string, UpgradeDef>,
    private mutations: MutationSystem,
  ) {}

  levels(): Record<string, number> {
    return saveManager.getRun().upgrades;
  }

  level(id: string): number {
    return saveManager.getRun().upgrades[id] ?? 0;
  }

  costOf(id: string): number {
    const def = this.defs[id];
    if (!def) return Infinity;
    const lvl = this.level(id);
    return Math.floor(def.baseCost * Math.pow(def.costScale, lvl));
  }

  canBuy(id: string): boolean {
    const def = this.defs[id];
    if (!def) return false;
    const lvl = this.level(id);
    if (def.maxLevel !== undefined && lvl >= def.maxLevel) return false;
    return player.biomass >= this.costOf(id);
  }

  buy(id: string): boolean {
    const def = this.defs[id];
    if (!def) return false;
    const cost = this.costOf(id);
    if (!player.spendBiomass(cost)) return false;
    const run = saveManager.getRun();
    run.upgrades[id] = (run.upgrades[id] ?? 0) + 1;
    bus.emit('upgrade:bought', { upgradeId: id, level: run.upgrades[id] });
    this.recompute();
    return true;
  }

  recompute(): void {
    player.recomputeStats(
      this.mutations.getEffects(),
      saveManager.getRun().upgrades,
      this.defs,
    );
  }

  allDefs(): UpgradeDef[] {
    return Object.values(this.defs);
  }
}
